import { db } from "@/api/db/db.index";
import { type LegalConsents, users } from "@/api/db/schema/users";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";
import { optionalAuthMiddlewareServer } from "../middleware/optional-auth";

export const CURRENT_TERMS_VERSION = "v1.0";

export function checkHasAcceptedAllConsents(
  consents?: LegalConsents | null,
): boolean {
  if (!consents) return false;
  return Boolean(
    consents.kvkk?.accepted &&
      consents.privacyPolicy?.accepted &&
      consents.workoutVideoConsent?.accepted,
  );
}

// Accept legal consents (KVKK, Privacy Policy, Workout Video Consent, Health Disclaimer)
export const acceptLegalConsentFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(
    z
      .object({
        version: z.string().default(CURRENT_TERMS_VERSION),
        acceptKvkk: z.boolean().default(true),
        acceptPrivacyPolicy: z.boolean().default(true),
        acceptWorkoutVideoConsent: z.boolean().default(true),
        acceptHealthDisclaimer: z.boolean().default(true),
      })
      .optional(),
  )
  .handler(async ({ context, data }) => {
    const userId = context.auth.userId;
    const version = data?.version || CURRENT_TERMS_VERSION;
    const nowIso = new Date().toISOString();

    // Fetch existing consents to preserve any previous flags if needed
    const existing = await db
      .select({ legalConsents: users.legalConsents })
      .from(users)
      .where(eq(users.id, userId))
      .limit(1);

    const existingConsents =
      (existing[0]?.legalConsents as LegalConsents) || {};

    const updatedConsents: LegalConsents = {
      ...existingConsents,
      kvkk: {
        accepted: data?.acceptKvkk ?? true,
        acceptedAt: nowIso,
        version,
      },
      privacyPolicy: {
        accepted: data?.acceptPrivacyPolicy ?? true,
        acceptedAt: nowIso,
        version,
      },
      workoutVideoConsent: {
        accepted: data?.acceptWorkoutVideoConsent ?? true,
        acceptedAt: nowIso,
        version,
      },
      healthDisclaimer: {
        accepted: data?.acceptHealthDisclaimer ?? true,
        acceptedAt: nowIso,
        version,
      },
    };

    await db
      .update(users)
      .set({
        legalConsents: updatedConsents,
        updatedAt: new Date(),
      })
      .where(eq(users.id, userId));

    return {
      status: 200,
      body: {
        success: true,
        legalConsents: updatedConsents,
        hasAcceptedAll: checkHasAcceptedAllConsents(updatedConsents),
      },
    };
  });

// Get legal consent status
export const getLegalConsentStatusFn = createServerFn({ method: "GET" })
  .middleware([optionalAuthMiddlewareServer])
  .handler(async ({ context }) => {
    if (!context.auth) {
      return {
        status: 200,
        body: {
          authenticated: false,
          hasAcceptedAll: false,
          legalConsents: null,
        },
      };
    }

    const user = await db
      .select({ legalConsents: users.legalConsents })
      .from(users)
      .where(eq(users.id, context.auth.userId))
      .limit(1);

    const consents = (user[0]?.legalConsents as LegalConsents) || null;
    const hasAcceptedAll = checkHasAcceptedAllConsents(consents);

    return {
      status: 200,
      body: {
        authenticated: true,
        hasAcceptedAll,
        legalConsents: consents,
      },
    };
  });

export const getLegalConsentStatusOptions = () =>
  queryOptions({
    queryKey: ["legalConsentStatus"],
    queryFn: () => getLegalConsentStatusFn(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
